import { useQuery } from '@apollo/client';

import { ProductType } from '@xing-com/crate-common-graphql-types';
import { GetCampaignDocument } from '@xing-com/crate-upsell-graphql';
import type { GetCampaignQuery } from '@xing-com/crate-upsell-graphql';
import { usePayOrConsentCampaignMid } from '@xing-com/crate-upsell-utils';
import { useFeatures } from '@xing-com/hub';

import { useLanguage } from './use-language';

export type CampaignState =
  | {
      status: 'loading';
      campaign: null;
      error?: undefined;
    }
  | {
      status: 'available';
      campaign: NonNullable<GetCampaignQuery['results']>;
      error?: undefined;
    }
  | {
      status: 'owned';
      campaign: NonNullable<GetCampaignQuery['results']> | null;
      error?: undefined;
    }
  | {
      status: 'unavailable';
      campaign: null;
      error?: undefined;
    }
  | {
      status: 'error';
      campaign: null;
      error: Error;
    };

type CardsCampaign = {
  campaigns: {
    premium: CampaignState;
    projobs: CampaignState;
  };
  errors: {
    premium?: Error;
    projobs?: Error;
  };
  loading: boolean;
  retry: () => Promise<void>;
};

export type CardsCampaignOptions = {
  mid: string;
  language: string;
};

export const useCardsCampaign = (
  product: ProductType,
  options?: CardsCampaignOptions
): CardsCampaign => {
  const hasExplicitContext = options !== undefined;
  const { loadingLanguage, language: viewerLanguage } = useLanguage({
    skip: hasExplicitContext,
  });
  const defaultMid = usePayOrConsentCampaignMid();
  const mid = options?.mid ?? defaultMid;
  const language = options?.language ?? viewerLanguage;
  const loadingContext = !hasExplicitContext && loadingLanguage;
  const featuresResult = useFeatures();
  const skip = !product || loadingContext;
  const variables = { mid, language };

  const premiumCampaign = useQuery(GetCampaignDocument, {
    skip,
    variables: { ...variables, product: ProductType.Premium },
  });
  const projobsCampaign = useQuery(GetCampaignDocument, {
    skip,
    variables: { ...variables, product: ProductType.Projobs },
  });

  const getCampaignState = (
    campaignResult: typeof premiumCampaign,
    campaignProduct: ProductType
  ): CampaignState => {
    if (loadingContext || featuresResult.loading || campaignResult.loading) {
      return { status: 'loading', campaign: null };
    }

    const error = campaignResult.error ?? featuresResult.error;
    if (error) {
      return {
        status: 'error',
        campaign: null,
        error,
      };
    }

    const isOwned =
      campaignProduct === ProductType.Premium
        ? featuresResult.data?.isPremium === true
        : featuresResult.data?.hasProJobsMembership === true;
    const campaign = campaignResult.data?.results ?? null;

    if (isOwned) {
      return { status: 'owned', campaign };
    }

    if (campaign) {
      return { status: 'available', campaign };
    }

    return {
      status: 'unavailable',
      campaign: null,
    };
  };

  return {
    campaigns: {
      premium: getCampaignState(premiumCampaign, ProductType.Premium),
      projobs: getCampaignState(projobsCampaign, ProductType.Projobs),
    },
    errors: {
      premium: premiumCampaign.error,
      projobs: projobsCampaign.error,
    },
    loading:
      loadingContext ||
      featuresResult.loading ||
      premiumCampaign.loading ||
      projobsCampaign.loading,
    retry: () =>
      Promise.all([premiumCampaign.refetch(), projobsCampaign.refetch()]).then(
        () => undefined
      ),
  };
};
